import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, CanLoad, Route, Router, RouterStateSnapshot, UrlSegment, UrlTree } from '@angular/router';
import { Observable } from 'rxjs';
import { ApiService } from './api.service';
import { AppService } from './app.service';
import { DeviceDetectorService } from 'ngx-device-detector';

@Injectable({
	providedIn: 'root'
})
export class AuthGuardService implements CanActivate, CanLoad {

	constructor(
		private app: AppService,
		private api: ApiService,
		private router: Router,
		private deviceService: DeviceDetectorService
	) { }

	canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Promise<boolean | UrlTree> {
		return new Promise((resolve, reject) => {
			this.app.org = route.params.org || null;

			this.api.refreshUserInfo(() => {
				// Check if user is logged in
				if (!this.app.userEmail) {
					reject();
					this.router.navigate(['/auth/login'], { queryParams: { redirect: state.url } });
					return;
				}

				// Check if user has access to the account
				if (!this.app.orgInfo) {
					resolve(this.router.parseUrl('/'));
					return;
				}

				const needsToAcceptTerms = this.app.orgInfo.role.id === 1
					&& !this.app.orgInfo.role.is_elevated
					&& !this.app.orgInfo.role.is_root
					&& !this.app.orgInfo.has_accepted_latest_terms;

				if (!route.data?.authGuardSkipTermsConditions) {
					if (needsToAcceptTerms) {
						resolve(this.router.parseUrl(`/${this.app.org}/terms-conditions/accept`));
						return;
					}
				}

				// Check if the account has run out (only if t&c is accepted to avoid race condition).
				// A cancelled account is treated exactly like an expired trial: it lands on the
				// same screen and can restart its subscription from there.
				//
				// This deliberately sits BEFORE the payment_overdue redirect further down. A
				// cancelled account can be flagged overdue too, and this screen has to win -
				// otherwise they get bounced to a billing page they can no longer act on.
				//
				// Two exceptions: WeQuote staff keep full access (they get a warning bar in the
				// header instead), and everyone keeps the handful of settings pages below.
				if (!route.data?.authGuardSkipExpired) {
					if (this.app.isLockedOutAccount
						&& !needsToAcceptTerms
						&& !this.app.orgInfo.role.is_elevated
						&& !this.isAllowedWhileLockedOut(state.url)) {
						resolve(this.router.parseUrl('/' + this.app.org + '/account/sandbox-expired'));
						return;
					}
				}

				// Check if mobile device
				if (!this.app.shouldSkipDeviceCheck) {
					if (this.deviceService.isMobile()) {
						resolve(this.router.parseUrl('/mobile'));
						return;
					}
				}

				// Check if account needs onboarding (only if T&C is accepted and trial hasn't expired to avoid race condition)
				if (route.data && !route.data.onboarding) {
					if (this.app.orgInfo.needs_onboarding && !this.app.isLockedOutAccount && !needsToAcceptTerms) {
						resolve(this.router.parseUrl('/' + this.app.orgInfo.alias + '/onboarding'));
						return;
					}
				}

				// Check if route is root users only
				if (route.data?.authGuardRootOnly) {
					if (!this.app.orgInfo.role.is_root) {
						resolve(this.router.parseUrl('/' + this.app.org));
						return;
					}
				}

				// Check if route is elevated users only
				if (route.data?.authGuardElevatedOnly) {
					if (!this.app.orgInfo.role.is_elevated) {
						resolve(this.router.parseUrl('/' + this.app.org));
						return;
					}
				}

				// Check if route requires the manage_notifications permission (root/elevated always allowed)
				if (route.data?.authGuardManageNotifications) {
					if (!this.app.orgInfo.role.manage_notifications
						&& !this.app.orgInfo.role.is_root
						&& !this.app.orgInfo.role.is_elevated) {
						resolve(this.router.parseUrl('/' + this.app.org));
						return;
					}
				}

				// Check if route needs the CRM (organisation flag plus role access)
				if (route.data?.authGuardCrm) {
					if (!this.app.crmEnabled) {
						resolve(this.router.parseUrl('/' + this.app.org));
						return;
					}
				}

				// Check if route is the main Dashboard
				if (route.data?.authGuardDashboard && !this.leaf(route).data?.authGuardSkipDashboard) {
					if (!this.app.dashboardEnabled) {
						resolve(this.router.parseUrl('/' + this.app.org));
						return;
					}
				}

				// If payment is overdue, redirect to billing page (only if T&C is accepted)
				if (route.data && !route.data.billing) {
					// Ignore payment overdue flag for root users
					if (this.app.orgInfo.payment_overdue && !this.app.orgInfo.role.is_root && !needsToAcceptTerms) {
						if (this.app.orgInfo.role.manage_billing) {
							// Got access to billing, redirect to payment page
							resolve(this.router.parseUrl('/' + this.app.orgInfo.alias + '/settings/billing'));
						} else {
							// Don't have access to billing
							resolve(this.router.parseUrl('/' + this.app.orgInfo.alias + '/settings'));
						}
					}
				}

				resolve(true);
			});
		});
	}

	canLoad(_route: Route, _segments: UrlSegment[]): boolean | UrlTree | Promise<boolean | UrlTree> | Observable<boolean | UrlTree> {
		// When the application is updated, lazy loded module files may no longer be there. This
		// results in a ChunkLoadError being thrown in the background, without triggering the
		// canActivate method of this route guard. That means we miss the chance to alert the
		// user that there is a new version, and navigation fails silently.

		// With a canLoad guard that runs the first time, we just run a useless server hit that
		// will resolve the new build number. This way we have a chance to alert the user before
		// the error gets thrown.

		this.api.general.ping();
		return true;
	}

	// The deepest child of a route snapshot - the page actually being opened.
	private leaf(route: ActivatedRouteSnapshot): ActivatedRouteSnapshot {
		while (route.firstChild) route = route.firstChild;
		return route;
	}

	// Pages a locked-out user keeps access to. Matched on the URL because the settings
	// children are lazy loaded and this guard runs on the parent :org/settings route,
	// which has no way of knowing which child is being activated.
	//
	// KEEP IN STEP with the $allowed list in api-organisation.php::navigation(), which
	// trims the settings menu to the same pages. A page in one list but not the other
	// either shows a menu item that bounces straight back to the lock screen, or hides
	// a page that would have worked - including the header's own links into settings.
	private isAllowedWhileLockedOut(url: string): boolean {
		const path = url.split(/[?#]/)[0].replace('/' + this.app.org, '').replace(/^\/|\/$/g, '');
		return ['settings', 'settings/profile', 'settings/company', 'settings/billing', 'settings/switch'].indexOf(path) !== -1;
	}

}
